/*
 * File: SiteInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 May 2021 2:13:24 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Select, Space, Spin, Typography } from 'antd';
import { CreateSalesOrderType } from 'common/enums/CreateSalesOrderType';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetSitesQuery } from 'redux/services/useKingstonLivingstonService';
import { BlockLoader } from '../BlockLoader';
const { Title } = Typography;

const styles: InlineStylesModel = {
  formItem: {
    marginBottom: 10,
    width: '100%'
  },
  formItemCol: {
    marginRight: '16px',
    maxHeight: '42px'
  },
  titleColor: {
    color: '#2F5596'
  }
};

export const SiteInput = (): JSX.Element => {
  const {
    values: { siteId },
    setFieldValue
  } = useFormikContext<SalesOrderPayloadModel>();
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  /* ****************** Hook API ****************** */
  const { data: sitesResponse, isFetching, isLoading } = useGetSitesQuery({ offset, dataAreaId, overrideSkipTake: true });

  /* ****************** JSX / Variables ****************** */
  const sitesSearchOptions = useMemo(() => sitesResponse?.data?.map((site) => ({ label: site.siteId, value: site.siteId })), [sitesResponse]);

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  // const handleSearch = useDebouncedCallback((value: string | undefined) => {
  //   _setOffset(0);
  //   _setInputValue(value);
  // }, 600);

  const handleClear = (): void => {
    _setInputValue(undefined);
    _setOffset(0);
    setFieldValue('siteId' as keyof SalesOrderPayloadModel, null);
    setFieldValue('warehouseId' as keyof SalesOrderPayloadModel, null);
  };

  // const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
  //   const target = event.currentTarget;

  //   if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && sitesResponse && sitesResponse?.totalCount - offset > 25) {
  //     _setOffset((prevState) => prevState + 25);
  //   }
  // };

  const handleSelect = (value: string): void => {
    setFieldValue('siteId' as keyof SalesOrderPayloadModel, value);
    setFieldValue('warehouseId' as keyof SalesOrderPayloadModel, null);
    _setInputValue(undefined);
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      (isLoading || isFetching) && offset ? (
        <div style={{ padding: '0px 10px' }}>
          <Space>
            <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ****************** Render ****************** */
  return (
    <>
      <Title level={5} style={styles.titleColor}>
        {CreateSalesOrderType.SITE}
      </Title>
      <Select
        value={siteId ?? undefined}
        allowClear
        dropdownMatchSelectWidth

        dropdownRender={dropDownRender}
        // filterOption={false}
        filterOption={(input, option): boolean => (option?.label as unknown as string).toLowerCase().includes(input.toLowerCase())}
        loading={isLoading || isFetching}
        onClear={handleClear}
        disabled={isPosted}
        // onPopupScroll={handleScroll}
        // onSearch={handleSearch}
        onSelect={handleSelect}
        options={sitesSearchOptions}
        showSearch
        style={styles.formItem}
      />
    </>
  );
};
